import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { IdentityCard } from '@/components/IdentityCard';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Input } from '@/components/ui/Input';
import { container } from '@/services/container';
import { useIdentityStore } from '@/store/identity';
import { useSettingsStore } from '@/store/settings';
import { useUiStore } from '@/store/ui';
import { MeshLinkError } from '@/lib/result';
import { AUTO_LOCK_OPTIONS } from '@/config';
import { ROUTE_PATH } from '@/router';

/** 「我的」Tab：DID 卡片 + 资料编辑 + 链上状态 + 锁定策略 + 入口导航。 */
export function MePage(): JSX.Element {
  const navigate = useNavigate();
  const { did, peerId, name, bio, registered, faucetClaimed, patch } = useIdentityStore();
  const autoLockMinutes = useSettingsStore((s) => s.autoLockMinutes);
  const setAutoLockMinutes = useSettingsStore((s) => s.setAutoLockMinutes);
  const lock = useIdentityStore((s) => s.lock);

  const [editing, setEditing] = useState(false);
  const [editName, setEditName] = useState('');
  const [editBio, setEditBio] = useState('');
  const [followCount, setFollowCount] = useState(0);
  const [saving, setSaving] = useState(false);
  const [checkingIn, setCheckingIn] = useState(false);
  const [checkinState, setCheckinState] = useState<{ today: boolean; streak: number }>(() => {
    try {
      const raw = window.localStorage.getItem('mesh_checkin');
      const parsed = raw ? (JSON.parse(raw) as { date: string; streak: number }) : null;
      const today = new Date().toISOString().slice(0, 10);
      if (!parsed) return { today: false, streak: 0 };
      if (parsed.date === today) return { today: true, streak: parsed.streak };
      // 连续判定：昨天签到则递增，否则重置
      const yesterday = new Date(Date.now() - 86_400_000).toISOString().slice(0, 10);
      const streak = parsed.date === yesterday ? Math.min(7, parsed.streak + 1) : 1;
      return { today: false, streak };
    } catch {
      return { today: false, streak: 0 };
    }
  });

  useEffect(() => {
    void container.social
      .getFollowing()
      .then((list) => setFollowCount(list.length))
      .catch(() => setFollowCount(0));
  }, []);

  /** 每日签到：checkin 上链 → 签到挖矿（首日 0.0005 / 连续 ≥2 天 0.001 DPB）。 */
  const doCheckin = async (): Promise<void> => {
    if (checkingIn || checkinState.today) return;
    const addr = container.identity.address;
    if (!addr) {
      useUiStore.getState().pushError('E_LOCKED');
      return;
    }
    setCheckingIn(true);
    try {
      await container.chain.checkin({
        from: addr as `0x${string}`,
        ts: Date.now(),
        signature: container.identity.signObject({ checkin: addr, ts: Date.now() }),
      });
      const streak = checkinState.streak === 0 ? 1 : Math.min(7, checkinState.streak + 1);
      try {
        window.localStorage.setItem(
          'mesh_checkin',
          JSON.stringify({ date: new Date().toISOString().slice(0, 10), streak }),
        );
      } catch {
        /* 存储满忽略 */
      }
      setCheckinState({ today: true, streak });
      useUiStore.getState().pushToast('success', `签到成功（连续 ${streak} 天，奖励已上链）`);
    } catch (err) {
      useUiStore.getState().pushError(err instanceof MeshLinkError ? err.code : 'E_UNKNOWN');
    } finally {
      setCheckingIn(false);
    }
  };

  const openEdit = (): void => {
    setEditName(name);
    setEditBio(bio ?? '');
    setEditing(true);
  };

  const saveProfile = async (): Promise<void> => {
    setSaving(true);
    try {
      const next = await container.identity.updateProfile({
        name: editName.trim(),
        bio: editBio.trim(),
      });
      patch({ name: next.name, bio: next.bio });
      useUiStore.getState().pushToast('success', '资料已更新');
      setEditing(false);
    } catch (err) {
      useUiStore.getState().pushError(err instanceof MeshLinkError ? err.code : 'E_UNKNOWN');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <IdentityCard did={did} peerId={peerId} name={name} />

      <div className="card p-5">
        <h2 className="mb-3 text-sm font-medium text-text">链上状态</h2>
        <div className="flex flex-wrap gap-2">
          <Badge tone={registered ? 'ok' : 'warn'}>{registered ? 'DID 已注册' : 'DID 未注册'}</Badge>
          <Badge tone={faucetClaimed ? 'gold' : 'neutral'}>
            {faucetClaimed ? '新手奖励已领取' : '未领取新手奖励'}
          </Badge>
          <Badge tone="neutral">关注 {followCount}</Badge>
        </div>

        <h2 className="mb-3 mt-6 text-sm font-medium text-text">每日签到（签到即挖矿）</h2>
        <div className="flex items-center justify-between gap-2">
          <div className="text-xs text-dim">
            {checkinState.today ? (
              <span className="text-ok">今日已签到 · 连续 {checkinState.streak} 天 ✓</span>
            ) : (
              <span>
                连续 {checkinState.streak} 天 · 首日 0.0005 DPB，连续奖励 0.001 DPB（7 天封顶）
              </span>
            )}
          </div>
          <Button size="sm" loading={checkingIn} disabled={checkinState.today} onClick={() => void doCheckin()}>
            {checkinState.today ? '已签到 ✓' : '签到'}
          </Button>
        </div>

        <h2 className="mb-3 mt-6 text-sm font-medium text-text">资料</h2>        {editing ? (
          <div className="space-y-3">
            <Input value={editName} onChange={(e) => setEditName(e.target.value)} placeholder="昵称" />
            <Input value={editBio} onChange={(e) => setEditBio(e.target.value)} placeholder="简介" />
            <div className="flex gap-2">
              <Button size="sm" loading={saving} onClick={() => void saveProfile()}>
                保存
              </Button>
              <Button variant="ghost" size="sm" onClick={() => setEditing(false)}>
                取消
              </Button>
            </div>
          </div>
        ) : (
          <div>
            <p className="text-sm text-text">{name || '未设置昵称'}</p>
            <p className="mt-1 text-xs text-dim">{bio || '未填写简介'}</p>
            <Button variant="ghost" size="sm" className="mt-2" onClick={openEdit}>
              编辑资料
            </Button>
          </div>
        )}

        <h2 className="mb-3 mt-6 text-sm font-medium text-text">自动锁定</h2>
        <div className="flex flex-wrap gap-2">
          {AUTO_LOCK_OPTIONS.map((m) => (
            <button
              key={m}
              type="button"
              onClick={() => setAutoLockMinutes(m)}
              className={`rounded-btn px-3 py-1.5 text-xs ${
                autoLockMinutes === m ? 'bg-accent text-[#04141c]' : 'bg-panel-2 text-dim hover:bg-line'
              }`}
            >
              {m} 分钟
            </button>
          ))}
        </div>

        <h2 className="mb-3 mt-6 text-sm font-medium text-text">我的</h2>
        <div className="flex flex-wrap gap-2">
          <Button variant="ghost" size="sm" onClick={() => navigate(ROUTE_PATH.wallet)}>
            钱包
          </Button>
          <Button variant="ghost" size="sm" onClick={() => navigate(ROUTE_PATH.settings)}>
            设置
          </Button>
          <Button variant="ghost" size="sm" onClick={() => navigate(ROUTE_PATH.security)}>
            安全中心
          </Button>
          <Button variant="ghost" size="sm" onClick={() => navigate(ROUTE_PATH.discover)}>
            发现
          </Button>
        </div>

        <div className="mt-6 flex gap-2">
          <Button variant="danger" size="sm" onClick={lock}>
            立即锁定
          </Button>
        </div>
      </div>
    </div>
  );
}

export default MePage;
